import { useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import { Dialog, DialogContent, DialogActions, Button, Typography, Box, IconButton, CircularProgress } from '@mui/material';

const DeleteConfirmDialog = ({ open, onClose, onConfirm, itemName = 'item', loading = false }) => {
  const [internalLoading, setInternalLoading] = useState(false);
  const isLoading = loading || internalLoading;

  const handleClose = (event, reason) => {
    if (isLoading) return;
    if (onClose) {
      onClose(event, reason);
    }
  };

  const handleConfirm = async (e) => {
    if (isLoading) return;
    if (!onConfirm) return;
    try {
      const result = onConfirm(e);
      if (result && typeof result.then === 'function') {
        setInternalLoading(true);
        await result;
      }
    } finally {
      setInternalLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      slotProps={{
        backdrop: {
          style: { backgroundColor: 'rgba(15, 23, 42, 0.4)', backdropFilter: 'blur(2px)' }
        }
      }}
      PaperProps={{
        sx: {
          borderRadius: 4,
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          overflow: 'hidden'
        }
      }}
    >
      <IconButton
        onClick={handleClose}
        disabled={isLoading}
        size="small"
        sx={{
          position: 'absolute',
          right: 12,
          top: 12,
          color: '#94a3b8',
          '&.Mui-disabled': { color: '#cbd5e1' }
        }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>

      <DialogContent sx={{ textAlign: 'center', pt: 5, px: 4 }}>
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            bgcolor: '#fef2f2',
            color: '#ef4444',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto',
            mb: 3,
            boxShadow: '0 0 0 8px #fff5f5' // Adds a nice expanded soft ring
          }}
        >
          <DeleteIcon sx={{ fontSize: 32 }} />
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: '#1e293b' }}>
          Delete {itemName.charAt(0).toUpperCase() + itemName.slice(1)}
        </Typography>
        <Typography variant="body2" sx={{ color: '#64748b', lineHeight: 1.6 }}>
          Are you sure you want to delete this {itemName}? This action cannot be undone and will permanently remove it from the system.
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 4, pb: 4, pt: 1, justifyContent: 'center', gap: 2 }}>
        <Button
          onClick={handleClose}
          disabled={isLoading}
          variant="outlined"
          disableElevation
          sx={{
            flex: 1,
            fontWeight: 600,
            borderRadius: 2,
            color: '#475569',
            borderColor: '#cbd5e1',
            textTransform: 'none',
            fontSize: '0.95rem',
            padding: '8px 16px',
            '&:hover': {
              backgroundColor: '#f8fafc',
              borderColor: '#94a3b8'
            }
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={isLoading}
          color="error"
          variant="contained"
          disableElevation
          startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{
            flex: 1,
            fontWeight: 600,
            borderRadius: 2,
            textTransform: 'none',
            fontSize: '0.95rem',
            padding: '8px 16px',
            backgroundColor: '#ef4444',
            '&:hover': {
              backgroundColor: '#dc2626'
            },
            '&.Mui-disabled': {
              backgroundColor: '#fca5a5',
              color: '#ffffff'
            }
          }}
        >
          {isLoading ? 'Deleting...' : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteConfirmDialog;
